/**
 * QuotePanel + QuoteModal — Combined file. Mirrors ChangeOrderPanel/Modal
 * but generates a full REPAIR QUOTE & PROPOSAL PDF (like the ProCall 6220
 * template). Panel shows Draft/Finalized/Accepted status, share-link
 * actions (Copy / SMS / Email / Preview / Rotate / Revoke), and drives
 * the create/edit modal.
 */
import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Sparkle, FilePdf, Copy, EnvelopeSimple, PaperPlaneTilt, Eye,
  Trash, ArrowClockwise, CheckCircle, LockKey, LockKeyOpen, Warning, X, FloppyDisk,
} from "@phosphor-icons/react";

const money = (c) => {
  const v = Math.abs(c || 0) / 100;
  return v % 1 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : `$${Math.round(v).toLocaleString()}`;
};

const STATUS_TONE = {
  draft:     { bg: "bg-slate-200 text-slate-700",   icon: LockKeyOpen, label: "Draft" },
  finalized: { bg: "bg-blue-100 text-blue-800",     icon: LockKey,     label: "Finalized" },
  accepted:  { bg: "bg-emerald-100 text-emerald-800", icon: CheckCircle, label: "Accepted" },
};

const emptyLine = () => ({ description: "", details: "", amount_cents: 0 });

const DEFAULT_TERMS = [
  "Quote valid for 14 days from date above.",
  "Payment Schedule (3 payments):",
  "  • 40% due upon signed agreement and mobilization/start of work",
  "  • 40% due upon substantial completion of work (with photo documentation)",
  "  • 20% final payment due after owner final walk-through, punch list completion, and unconditional lien waivers",
  "Payments will be processed through the AppFolio owner portal. This agreement becomes effective and valid upon receipt of the initial 40% down payment.",
  "Work to commence promptly upon signed agreement and receipt of the initial 40% payment.",
  "Includes all labor, materials, and cleanup. Excludes permits (if required — owner responsibility) and any unforeseen hidden conditions (handled via change order).",
  "1-year workmanship warranty on completed work. Manufacturer warranties passed through where applicable.",
];

export default function QuotePanel({ job }) {
  const jobId = job.id;
  const [items, setItems] = useState([]);
  const [loading, setLoad] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoad(true);
    try { setItems((await api.get(`/jobs/${jobId}/quotes`)).data.items || []); }
    catch { setItems([]); }
    finally { setLoad(false); }
  };
  useEffect(() => { load(); }, [jobId]); // eslint-disable-line
  useEffect(() => {
    const h = () => load();
    window.addEventListener("fr:quote-created", h);
    return () => window.removeEventListener("fr:quote-created", h);
  }, []); // eslint-disable-line

  const remove = async (q) => {
    if (!window.confirm("Delete this quote?")) return;
    try { await api.delete(`/quotes/${q.id}`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Delete failed")); }
  };
  const toOwner = async (q) => {
    const to_email = window.prompt("Owner email (blank = landlord linked to this property):", "");
    if (to_email === null) return;
    try {
      if (q.needs_review) await api.post(`/quotes/${q.id}/review-ok`);
      const r = await api.post(`/jobs/${jobId}/request-owner-approval`, { estimate_cents: q.subtotal_cents, to_email, note: q.title });
      toast.success(`Approve/Reject email sent to ${r.data?.to_email || to_email || "owner"}`); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not send to owner")); }
  };
  const reviewOk = async (q) => {
    try { await api.post(`/quotes/${q.id}/review-ok`); toast.success("Reviewed — ready to finalize & send"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };
  const finalize = async (q) => {
    if (q.needs_review && !window.confirm("This proposal hasn't been reviewed yet. Finalize anyway?")) return;
    try { await api.post(`/quotes/${q.id}/finalize`); toast.success("Finalized"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };
  const rotate = async (q) => {
    try { await api.post(`/quotes/${q.id}/share-link?rotate=true`); toast.success("Rotated"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };
  const revoke = async (q) => {
    if (!window.confirm("Kill this public link?")) return;
    try { await api.delete(`/quotes/${q.id}/share-link`); toast.success("Revoked"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };

  return (
    <div className="mt-4 rounded-md border-2 border-blue-200 bg-blue-50/50 p-3" data-testid="quote-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <FilePdf weight="fill" size={18} className="text-blue-700"/>
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-widest text-blue-800">Repair Quote &amp; Proposal</div>
            <div className="text-[10px] text-slate-500">
              {items.length ? `${items.length} quote${items.length === 1 ? "" : "s"} on this WO` : "Send owners a branded quote PDF instead of Google Docs"}
            </div>
          </div>
        </div>
        <button onClick={() => setModal("new")}
          className="h-9 px-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1 shadow-[3px_3px_0_rgba(0,0,0,0.35)]"
          data-testid="quote-new-btn">
          <Plus weight="bold" size={12}/> New Quote
        </button>
      </div>
      {loading ? (
        <div className="text-xs text-slate-400 mt-2">Loading…</div>
      ) : items.length ? (
        <div className="mt-3 space-y-2">
          {items.map(q => <QuoteRow key={q.id} q={q} onEdit={() => setModal(q)}
            onDelete={() => remove(q)} onFinalize={() => finalize(q)} onReviewOk={() => reviewOk(q)} onToOwner={() => toOwner(q)}
            onRotate={() => rotate(q)} onRevoke={() => revoke(q)}/>)}
        </div>
      ) : null}
      {modal && (
        <QuoteModal jobId={jobId} existing={modal === "new" ? null : modal}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>
      )}
    </div>
  );
}

function QuoteRow({ q, onEdit, onDelete, onFinalize, onRotate, onRevoke, onReviewOk, onToOwner }) {
  const [copied, setCopied] = useState(false);
  const tone = STATUS_TONE[q.status] || STATUS_TONE.draft;
  const StatusIcon = tone.icon;
  const url = q.share_token ? `${window.location.origin}/api/public/quote/${q.share_token}/pdf` : "";
  const label = `Quote — ${q.parent_property_address}${q.parent_unit ? ` #${q.parent_unit}` : ""}`;
  const sms = `${label}\n${money(q.subtotal_cents || 0)}${q.is_not_to_exceed ? " (Not To Exceed)" : ""}\n${url}`;
  const emailBody = `${label}:\n${url}\n\nPlease review and sign to move forward.\n\nField Radar`;

  const copy = async () => { if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); } };
  const preview = async () => {
    const t = localStorage.getItem("access_token");
    try {
      const res = await fetch(`${API}/quotes/${q.id}/pdf`, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error();
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch { toast.error("Preview failed"); }
  };

  return (
    <div className="rounded-md bg-white border-2 border-slate-200 overflow-hidden">
      <div className="p-2 flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${tone.bg}`}>
          <StatusIcon weight="fill" size={10}/> {tone.label}
        </span>
        {q.needs_review && (
          <button onClick={onReviewOk} title="Mark reviewed — then Finalize to send"
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-200 text-amber-900 hover:bg-amber-300"
            data-testid={`quote-needs-review-${q.id}`}>
            ⚠ Needs review · click when OK
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-black text-slate-900">{q.title || "Repair Quote & Proposal"}</div>
          <div className="text-[10px] text-slate-500">
            {money(q.subtotal_cents || 0)}{q.is_not_to_exceed ? " · Not To Exceed" : ""}
            {q.share_view_count > 0 ? ` · ${q.share_view_count} view${q.share_view_count === 1 ? "" : "s"}` : ""}
            {q.accepted_at ? ` · accepted by ${q.accepted_by_name}` : ""}
          </div>
        </div>
        <button onClick={preview} className="h-8 px-2 border border-slate-300 text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1"
          data-testid={`quote-preview-${q.id}`}>
          <Eye weight="bold" size={10}/> Preview
        </button>
        {q.status === "draft" && (
          <>
            <button onClick={onEdit} className="h-8 px-2 border border-blue-300 text-[10px] font-black uppercase tracking-widest text-blue-700 inline-flex items-center gap-1"
              data-testid={`quote-edit-${q.id}`}>Edit</button>
            <button onClick={onToOwner} title="Email the owner an Approve / Reject link for this amount"
              className="h-8 px-2 bg-slate-900 hover:bg-slate-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1"
              data-testid={`quote-owner-${q.id}`}>→ Owner approval</button>
            <button onClick={onFinalize} className="h-8 px-2 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1"
              data-testid={`quote-finalize-${q.id}`}>
              <LockKey weight="fill" size={10}/> Finalize
            </button>
            <button onClick={onDelete} className="h-8 w-8 text-slate-400 hover:text-red-500 flex items-center justify-center"
              data-testid={`quote-delete-${q.id}`}>
              <Trash weight="bold" size={12}/>
            </button>
          </>
        )}
      </div>
      {q.share_token && !q.share_revoked && (
        <div className="border-t border-slate-100 bg-blue-50/50 p-2 flex items-center gap-1 flex-wrap">
          <button onClick={copy} className="h-7 px-2 bg-blue-600 hover:bg-blue-700 text-white text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1"
            data-testid={`quote-copy-${q.id}`}>
            <Copy weight="bold" size={10}/> {copied ? "Copied" : "Copy Link"}
          </button>
          <a href={`sms:?&body=${encodeURIComponent(sms)}`} className="h-7 px-2 bg-white border border-blue-300 text-blue-700 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1"
            data-testid={`quote-sms-${q.id}`}>
            <PaperPlaneTilt weight="fill" size={10}/> SMS
          </a>
          <a href={`mailto:?subject=${encodeURIComponent(label)}&body=${encodeURIComponent(emailBody)}`}
            className="h-7 px-2 bg-white border border-blue-300 text-blue-700 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1"
            data-testid={`quote-email-${q.id}`}>
            <EnvelopeSimple weight="bold" size={10}/> Email
          </a>
          <button onClick={() => window.open(url, "_blank")} className="h-7 px-2 bg-white border border-blue-300 text-blue-700 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1">
            <Eye weight="bold" size={10}/> Open Public
          </button>
          <div className="flex-1"/>
          <button onClick={onRotate} title="Rotate" className="h-7 w-7 text-slate-500 hover:text-slate-900 flex items-center justify-center">
            <ArrowClockwise weight="bold" size={12}/>
          </button>
          <button onClick={onRevoke} title="Revoke" className="h-7 w-7 text-slate-500 hover:text-red-600 flex items-center justify-center">
            <Trash weight="bold" size={12}/>
          </button>
        </div>
      )}
      {q.share_revoked && (
        <div className="border-t border-red-100 bg-red-50 p-1.5 text-[10px] text-red-800 flex items-center gap-1">
          <Warning weight="fill" size={11}/> Public link revoked
        </div>
      )}
    </div>
  );
}

function QuoteModal({ jobId, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [f, setF] = useState({
    title: existing?.title || "Repair Quote & Proposal",
    prepared_for: existing?.prepared_for || "",
    prepared_by: existing?.prepared_by || "",
    project_overview: existing?.project_overview || "",
    scope_bullets: existing?.scope_bullets?.length ? existing.scope_bullets : [""],
    line_items: existing?.line_items?.length ? existing.line_items : [emptyLine()],
    is_not_to_exceed: existing?.is_not_to_exceed ?? true,
    note: existing?.note || "",
    terms_bullets: existing?.terms_bullets?.length ? existing.terms_bullets : DEFAULT_TERMS,
    valid_days: existing?.valid_days || 14,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (v) => setF(s => ({ ...s, [k]: v }));
  const updLine = (i, k, v) => setF(s => ({ ...s, line_items: s.line_items.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addLine = () => setF(s => ({ ...s, line_items: [...s.line_items, emptyLine()] }));
  const rmLine = (i) => setF(s => ({ ...s, line_items: s.line_items.filter((_, idx) => idx !== i) }));
  const updList = (key) => (i, v) => setF(s => ({ ...s, [key]: s[key].map((x, idx) => idx === i ? v : x) }));
  const addList = (key) => () => setF(s => ({ ...s, [key]: [...s[key], ""] }));
  const rmList = (key) => (i) => setF(s => ({ ...s, [key]: s[key].filter((_, idx) => idx !== i) }));

  const subtotal = useMemo(
    () => (f.line_items || []).reduce((a, x) => a + (parseInt(x.amount_cents) || 0), 0),
    [f.line_items]
  );

  const build = () => ({
    ...f,
    scope_bullets: f.scope_bullets.filter(x => x.trim()),
    terms_bullets: f.terms_bullets.filter(x => x.trim()),
    line_items: f.line_items.filter(x => x.description.trim() || x.amount_cents),
  });

  const save = async (finalize = false) => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      let q = isEdit
        ? (await api.patch(`/quotes/${existing.id}`, build())).data
        : (await api.post(`/jobs/${jobId}/quotes`, build())).data;
      if (finalize) {
        q = (await api.post(`/quotes/${q.id}/finalize`)).data;
        toast.success("Quote finalized & link ready");
      } else toast.success("Draft saved");
      onSaved?.(q);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  const setAmt = (i, str) => {
    const clean = String(str || "").replace(/[^\d.]/g, "");
    updLine(i, "amount_cents", clean ? Math.round(parseFloat(clean) * 100) : 0);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-[85] flex items-start justify-center p-2 sm:p-4" data-testid="quote-modal">
      <div className="bg-white w-full max-w-3xl max-h-[95vh] flex flex-col border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] overflow-hidden">
        <div className="p-3 bg-gradient-to-r from-blue-700 to-indigo-800 text-white flex items-start justify-between sticky top-0 z-10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
              <Sparkle weight="fill" size={11}/> Repair Quote Generator
            </div>
            <div className="font-black uppercase tracking-widest text-lg">{isEdit ? "Edit Quote" : "New Quote"}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-black/20" data-testid="quote-close"><X size={20}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Title</label>
            <input value={f.title} onChange={e => upd("title")(e.target.value)}
              className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm outline-none"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Prepared For</label>
              <input value={f.prepared_for} onChange={e => upd("prepared_for")(e.target.value)}
                placeholder="e.g. Ajua &amp; Omar Hawkins — Owners of 6220 Carpenter St"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm outline-none"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Valid Days</label>
              <input type="number" value={f.valid_days} onChange={e => upd("valid_days")(parseInt(e.target.value) || 14)}
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm outline-none"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Project Overview (paragraph)</label>
            <textarea rows={3} value={f.project_overview} onChange={e => upd("project_overview")(e.target.value)}
              className="mt-1 w-full p-2 border-2 border-slate-300 text-sm outline-none"/>
          </div>
          <BulletList label="Scope of Work" items={f.scope_bullets} onChange={updList("scope_bullets")} onAdd={addList("scope_bullets")} onRemove={rmList("scope_bullets")} testKey="scope"/>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                Itemized Pricing · Subtotal: <b className="text-slate-900">{money(subtotal)}</b>
              </label>
              <button onClick={addLine} className="text-[10px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
                <Plus weight="bold" size={11}/> Add Line
              </button>
            </div>
            <div className="space-y-1">
              {f.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-1">
                  <input value={li.description} onChange={e => updLine(i, "description", e.target.value)}
                    placeholder="Item / Description"
                    className="col-span-4 h-9 px-2 border border-slate-200 text-sm"/>
                  <input value={li.details} onChange={e => updLine(i, "details", e.target.value)}
                    placeholder="Details"
                    className="col-span-5 h-9 px-2 border border-slate-200 text-sm"/>
                  <input value={li.amount_cents ? (li.amount_cents / 100).toString() : ""}
                    onChange={e => setAmt(i, e.target.value)}
                    placeholder="0"
                    className="col-span-2 h-9 px-2 border border-slate-200 text-sm text-right"/>
                  <button onClick={() => rmLine(i)} className="col-span-1 text-slate-400 hover:text-red-500 flex items-center justify-center">
                    <Trash weight="bold" size={12}/>
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-md bg-blue-100 border-2 border-blue-300 p-3 flex items-center justify-between">
            <div>
              <div className="text-xs font-black uppercase tracking-widest text-blue-900">Total Quote Price</div>
              <label className="text-[10px] text-blue-700 flex items-center gap-1 mt-1">
                <input type="checkbox" checked={f.is_not_to_exceed} onChange={e => upd("is_not_to_exceed")(e.target.checked)}/>
                Show as "Not To Exceed"
              </label>
            </div>
            <div className="text-2xl font-black text-blue-900">{money(subtotal)}</div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Note</label>
            <textarea rows={2} value={f.note} onChange={e => upd("note")(e.target.value)}
              placeholder="e.g. Not To Exceed $36,000. Additional work notified before proceeding..."
              className="mt-1 w-full p-2 border-2 border-slate-300 text-sm"/>
          </div>
          <BulletList label="Terms &amp; Conditions" items={f.terms_bullets} onChange={updList("terms_bullets")} onAdd={addList("terms_bullets")} onRemove={rmList("terms_bullets")} testKey="terms"/>
        </div>

        <div className="p-3 border-t-2 border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2">
          <div className="flex-1"/>
          <button onClick={() => save(false)} disabled={busy}
            className="h-10 px-3 bg-white border-2 border-blue-400 text-blue-700 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1"
            data-testid="quote-save-draft">
            <FloppyDisk weight="fill" size={12}/> Save Draft
          </button>
          <button onClick={() => save(true)} disabled={busy}
            className="h-10 px-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1 shadow-[3px_3px_0_#0f172a]"
            data-testid="quote-finalize-btn">
            <LockKey weight="fill" size={12}/> Save &amp; Finalize
          </button>
        </div>
      </div>
    </div>
  );
}

function BulletList({ label, items, onChange, onAdd, onRemove, testKey }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500" dangerouslySetInnerHTML={{ __html: label }}/>
        <button onClick={onAdd} className="text-[10px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
          <Plus weight="bold" size={11}/> Add
        </button>
      </div>
      <div className="space-y-1">
        {items.map((b, i) => (
          <div key={i} className="flex gap-1">
            <input value={b} onChange={e => onChange(i, e.target.value)}
              className="flex-1 h-9 px-2 border border-slate-200 text-sm bg-white"
              data-testid={`quote-${testKey}-${i}`}/>
            <button onClick={() => onRemove(i)} className="w-8 text-slate-400 hover:text-red-500 flex items-center justify-center">
              <Trash weight="bold" size={12}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
